// Salesforce Enhanced Chat (MIAW) backend for the support widget (TWA-273).
// beacon.js routes here when the build sets VUE_APP_SF_CHAT_ORG_ID; prod keeps Beacon.
//
// Beacon's session-data becomes hidden pre-chat fields. Like Beacon, the values are a
// snapshot: Salesforce copies them onto the Messaging Session once, when the chat opens,
// so they must be set before the student clicks "Contact TWA" (an update after that does
// not reach the record).

const ORG_ID = process.env.VUE_APP_SF_CHAT_ORG_ID
const DEPLOYMENT = process.env.VUE_APP_SF_CHAT_DEPLOYMENT
const SITE_URL = process.env.VUE_APP_SF_CHAT_SITE_URL
const SCRT2_URL = process.env.VUE_APP_SF_CHAT_SCRT2_URL

export const enabled = Boolean(ORG_ID && DEPLOYMENT && SITE_URL && SCRT2_URL)

// The hidden pre-chat fields configured on the channel: Beacon's key with spaces turned
// into underscores. setHiddenPrechatFields rejects names the deployment doesn't define,
// so anything else is dropped here rather than failing the whole call.
const FIELDS = [
  'Flow', 'Course', 'Domain', 'Course_Id', 'Token', 'Email',
  'User_Message', 'Error_Context', 'Error_Code', 'Error_Message', 'HTTP_Status',
  'Access_Code', 'Term', 'Enrollment_Blocked', 'Course_Token', 'Search'
]

// Current field values, kept here because the widget forgets them whenever it
// re-initialises (page load, and again after every chat ends).
const fields = {}
let initialized = false
let ready = false
// "Contact TWA" clicked before the widget was ready: open it once it is, the way
// Beacon's stub queued calls made before its script loaded.
let openPending = false

function fieldName(key) {
  return key.replace(/ /g, '_')
}

function prechatAPI() {
  return window.embeddedservice_bootstrap && window.embeddedservice_bootstrap.prechatAPI
}

function apply(removed = []) {
  const api = prechatAPI()
  if (!ready || !api) return

  // Runs inside the views' error-reporting catch blocks, so a rejected value must not
  // throw out of them.
  try {
    if (removed.length) api.removeHiddenPrechatFields(removed)
    if (Object.keys(fields).length) api.setHiddenPrechatFields({ ...fields })
  } catch (err) {
    console.error('Salesforce chat: could not set hidden pre-chat fields: ', err)
  }
}

function launch() {
  openPending = false
  const failed = err => console.error('Salesforce chat: could not open the chat: ', err)
  // launchChat returns a Promise, so a refused launch arrives as a rejection.
  try {
    Promise.resolve(window.embeddedservice_bootstrap.utilAPI.launchChat()).catch(failed)
  } catch (err) {
    failed(err)
  }
}

export function init() {
  // The LTI tool screens call this from every failed request, and the bootstrap
  // global only appears once the async script has loaded, so guard on our own flag.
  if (initialized) return
  initialized = true

  // Fires on load and again after each chat ends, which clears the hidden fields.
  window.addEventListener('onEmbeddedMessagingReady', () => {
    ready = true
    apply()
    if (openPending) launch()
  })
  // The script loads but init is refused (e.g. a host the chat site doesn't allow):
  // without this a queued "Contact TWA" click waits forever with nothing logged.
  window.addEventListener('onEmbeddedMessagingInitError', err => {
    console.error('Salesforce chat: the chat could not start: ', err)
  })

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = `${SITE_URL}/assets/js/bootstrap.min.js`
  script.onerror = () => console.error('Salesforce chat: could not load the chat script')
  script.onload = () => {
    try {
      window.embeddedservice_bootstrap.settings.language = 'en_US'
      window.embeddedservice_bootstrap.init(ORG_ID, DEPLOYMENT, SITE_URL, { scrt2URL: SCRT2_URL })
    } catch (err) {
      console.error('Error loading Salesforce chat: ', err)
    }
  }
  document.head.appendChild(script)
}

// Merges data into the hidden fields. null/undefined clears a field, matching how
// beacon.js clears error keys; every value is sent as a string (the API takes strings only).
export function setData(data) {
  const removed = []
  Object.keys(data).forEach(key => {
    const name = fieldName(key)
    if (!FIELDS.includes(name)) {
      console.warn(`Salesforce chat: no hidden pre-chat field for '${key}'`)
      return
    }
    const value = data[key]
    if (value === null || value === undefined) {
      if (name in fields) {
        delete fields[name]
        removed.push(name)
      }
    } else {
      fields[name] = String(value)
    }
  })
  apply(removed)
}

export function open() {
  if (ready) {
    launch()
  } else {
    openPending = true
  }
}

export function identify(email) {
  setData({ 'Email': email })
}
